import { useAuth } from '@clerk/clerk-react';
import { useTrpcMutation, useTrpcQuery } from '@vrplatform/data';
import {
  CopyIcon,
  Edit3Icon,
  EmailIcon,
  EyeIcon,
  Link2Icon,
  RefreshCwIcon,
} from '@vrplatform/icons';
import {
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import { showSuccessNotification } from '@vrplatform/theme';
import { parseAsString, useQueryStates } from '@vrplatform/query-params';
import type { Maybe } from '@vrplatform/utils';
import { Box, Tooltip } from '@mantine/core';
import type { getOwnerStatus } from '@vrplatform/ui-common';
import { HiddenFeatureIndicator } from '../../components';

type Props = {
  owner: {
    id: Maybe<string>;
    teamId: Maybe<string>;
    firstName: Maybe<string>;
    lastName: Maybe<string>;
    companyName: Maybe<string>;
    email: Maybe<string>;
    status: ReturnType<typeof getOwnerStatus>['status'];
  };
  isReinvite: boolean;
  refetch: () => void;
};

function useOwnerHasPortalAccess({
  userId,
  teamId,
}: {
  userId: string;
  teamId: string;
}) {
  const { data, loading: loadingQuery } = useTrpcQuery(
    'fetchClerkUser',
    {
      teamId,
      userId: userId,
    },
    {
      skip: !userId || !teamId,
    }
  );

  const hasAccess = !!(
    (data?.publicMetadata?.allowed_ids || '') as string
  )?.includes(teamId);

  return {
    hasAccess,
    loadingQuery,
  };
}

export const UserAccessEllipsisMenuItems = ({
  isReinvite,
  ownerPortalContactId,
  owner: { id: userId, teamId, status },
  handlers: {
    setDeleteOpen,
    setInviteOpen,
    setRevokeAccessOpen,
    setCopyInviteOpen,
  },
}: Props & {
  ownerPortalContactId: Maybe<string>;
  handlers: {
    setInviteOpen: () => void;
    setCopyInviteOpen: () => void;
    setDeleteOpen: () => void;
    setRevokeAccessOpen: () => void;
  };
}) => {
  const [, setView] = useQueryStates({
    view: parseAsString,
    owner: parseAsString,
  });

  if (!userId || !teamId) return null;

  return (
    <>
      <EllipsisMenuItem
        onClick={() =>
          setView({
            view: 'edit',
          })
        }
        customIcon={<Edit3Icon size={18} />}
      >
        Edit user
      </EllipsisMenuItem>
      <EllipsisMenuDivider />

      <OwnerImpersonationEllipsisItem
        userId={userId}
        teamId={teamId}
        ownerPortalContactId={ownerPortalContactId}
        status={status}
      />

      <HiddenFeatureIndicator permission="partner-admin">
        <OwnerRevokeAccessMenuButton
          openModal={() => setRevokeAccessOpen()}
          userId={userId}
          teamId={teamId}
          status={status}
        />
      </HiddenFeatureIndicator>

      <EllipsisMenuDivider />
      <EllipsisMenuItem
        onClick={() => setInviteOpen()}
        customIcon={
          isReinvite ? <RefreshCwIcon size={18} /> : <EmailIcon size={18} />
        }
      >
        {isReinvite ? 'Resend email invitation' : 'Email invite to app'}
      </EllipsisMenuItem>

      {isReinvite && (
        <EllipsisMenuItem
          onClick={setCopyInviteOpen}
          customIcon={<Link2Icon size={18} />}
        >
          Copy invitation url
        </EllipsisMenuItem>
      )}

      <EllipsisMenuDivider />

      <EllipsisMenuDangerItem onClick={() => setDeleteOpen()}>
        Delete
      </EllipsisMenuDangerItem>

      <HiddenFeatureIndicator permission="super-admin">
        <EllipsisMenuItem
          customIcon={<CopyIcon size={18} />}
          disabled={!userId}
          onClick={() =>
            navigator.clipboard.writeText(`${userId}`).then(() =>
              showSuccessNotification({
                message: 'The ID was added to your clipboard.',
              })
            )
          }
        >
          Copy user ID
        </EllipsisMenuItem>
      </HiddenFeatureIndicator>
    </>
  );
};

type BaseMenuButtonProps = {
  userId: string;
  teamId: string;
  status: Props['owner']['status'];
};

interface OwnerRevokeAccessMenuButtonProps extends BaseMenuButtonProps {
  openModal: () => void;
}
const OwnerRevokeAccessMenuButton = ({
  openModal,
  userId,
  teamId,
}: OwnerRevokeAccessMenuButtonProps) => {
  const { hasAccess, loadingQuery } = useOwnerHasPortalAccess({
    userId,
    teamId,
  });

  return (
    <Tooltip
      disabled={hasAccess || loadingQuery}
      label="Owner does not currently have access to your team."
      withArrow
    >
      <Box>
        <EllipsisMenuItem
          onClick={openModal}
          icon={'UserCrossIcon'}
          loading={loadingQuery}
          disabled={!hasAccess}
        >
          Revoke Portal Access
        </EllipsisMenuItem>
      </Box>
    </Tooltip>
  );
};

const OwnerImpersonationEllipsisItem = ({
  userId,
  teamId,
  ownerPortalContactId,
  status,
}: BaseMenuButtonProps & {
  ownerPortalContactId: Maybe<string>;
}) => {
  const { signOut } = useAuth();

  const { hasAccess, loadingQuery } = useOwnerHasPortalAccess({
    userId,
    teamId,
  });

  const { mutate, loading } = useTrpcMutation('impersonateUser', {
    successMessage: {
      title: 'Owner Preview',
      message: 'Successfully created owner preview.',
    },
  });

  const createPreviewV2 = () =>
    mutate({ userId, tenantId: teamId }).then((res) => {
      const redirectUrl = new URL(res.url, window.location.origin);
      redirectUrl.searchParams.set('sti', teamId);
      if (ownerPortalContactId) {
        redirectUrl.searchParams.set('cti', ownerPortalContactId);
      }

      console.log('redirectUrl', redirectUrl.toString());

      signOut({
        redirectUrl: redirectUrl.toString(),
      });
    });

  return (
    <Tooltip
      disabled={hasAccess || loadingQuery}
      label={
        status === 'unconfirmed'
          ? 'Owner invite is currently pending'
          : 'Owner has not been invited to the owner portal.'
      }
      withArrow
    >
      <Box>
        <EllipsisMenuItem
          customIcon={<EyeIcon size={18} />}
          onClick={createPreviewV2}
          loading={loading || loadingQuery}
          disabled={!hasAccess}
        >
          User Impersonation
        </EllipsisMenuItem>
      </Box>
    </Tooltip>
  );
};
